import React, { useContext, useEffect, useState } from 'react'
import {
    Backdrop, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, IconButton, MenuItem, Stack, Switch, TextField, Typography
} from '@mui/material'
import { Add, Delete, Edit } from '@mui/icons-material'
import ClearIcon from '@mui/icons-material/Clear'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import Header from '../Components/layout/Header'
import PaymodeForm from '../Components/forms/pay-mode-form'
import { COMPTES_DE_CHARGES } from '../asset/variables'
import { API } from '../conf'
import { Context } from '../App'

const MOIS_LABELS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre']

function freqLabel(f) {
    if (!f) return ''
    switch (f.type) {
        case 'mensuel': return `Mensuel — le ${f.jour} de chaque mois`
        case 'trimestriel': return `Trimestriel — le ${f.jour} tous les 3 mois`
        case 'annuel': return `Annuel — le ${f.jour} ${MOIS_LABELS[(f.mois ?? 1) - 1]} chaque année`
        case 'libre': return `Libre — les ${[...(f.jours ?? [])].sort((a, b) => a - b).join(', ')} de chaque mois`
        default: return f.type
    }
}

function defaultLigne() {
    return {
        egliseNumber: '', montant: 0, compteType: 'liaison', compteNum: '',
        paymodeData: { useCustom: false, pay_mode: [''], especes: 0, repCheques: [{ banque: '', montant: 0, num_cheque: '' }], repVir: { date: '', montant: 0, ref: '' }, repPrev: { date: '', montant: 0, ref: '' }, reptotal: 0 }
    }
}

export default function DepensesCourantes() {
    const { updateConnect, exercice } = useContext(Context)
    const [templates, setTemplates] = useState([])
    const [eglises, setEglises] = useState([])
    const [comptesByEglise, setComptesByEglise] = useState({})
    const [loading, setLoading] = useState(true)
    const [creating, setCreating] = useState(false)
    const [editing, setEditing] = useState(null)
    const [sending, setSending] = useState(false)

    // --- États formulaire création ---
    const [libelle, setLibelle] = useState('')
    const [date, setDate] = useState(null)
    const [classe, setClasse] = useState('')
    const [fournisseur, setFournisseur] = useState('')
    const [numFacture, setNumFacture] = useState('')
    const [montantDefaut, setMontantDefaut] = useState(0)
    const [reptotal, setReptotal] = useState(0)
    const [payMode, setPayMode] = useState([''])
    const [especes, setEspeces] = useState(0)
    const [repCheques, setRepCheques] = useState([{ banque: '', montant: 0, num_cheque: '' }])
    const [repVir, setRepVir] = useState({ date: '', montant: 0, ref: '' })
    const [repPrev, setRepPrev] = useState({ date: '', montant: 0, ref: '' })
    const [lignes, setLignes] = useState([defaultLigne()])
    const [freqType, setFreqType] = useState('mensuel')
    const [freqJours, setFreqJours] = useState([1])

    // --- États formulaire édition ---
    const [editDate, setEditDate] = useState(null)
    const [editFreqType, setEditFreqType] = useState('mensuel')
    const [editFreqJours, setEditFreqJours] = useState([1])
    const [editLignes, setEditLignes] = useState([])

    function updateEditLigne(idx, key, value) {
        setEditLignes(old => old.map((l, i) => i === idx ? { ...l, [key]: value } : l))
    }

    function authHeaders() {
        return { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: 'Bearer ' + sessionStorage.getItem('token') }
    }

    function handleTokenRefresh(response) {
        const token = response.headers.get('x-auth-token')
        if (response.status === 401) { sessionStorage.clear(); updateConnect(false); return false }
        if (token && token !== sessionStorage.getItem('token') && token !== '') sessionStorage.setItem('token', token)
        return true
    }

    useEffect(() => {
        async function load() {
            const [tRes, eRes] = await Promise.all([
                fetch(API.concat('/depenses-courantes'), { method: 'GET', headers: authHeaders() }),
                fetch(API.concat('/eglises/exercice?' + new URLSearchParams({ annee: exercice }).toString()), {
                    method: 'POST', headers: authHeaders(), body: JSON.stringify({ type: 'église' })
                })
            ])
            if (!handleTokenRefresh(tRes) || !handleTokenRefresh(eRes)) return
            if (tRes.ok) setTemplates(await tRes.json())
            if (eRes.ok) setEglises((await eRes.json()).filter(e => e.type === 'église'))
            setLoading(false)
        }
        load()
    }, [exercice])

    async function fetchComptesEglise(egliseNumber) {
        if (comptesByEglise[egliseNumber] || !egliseNumber) return
        const res = await fetch(API.concat('/comptes/by?' + new URLSearchParams({ eglise: egliseNumber, exercice }).toString()), { method: 'GET', headers: authHeaders() })
        if (res.ok) {
            const data = await res.json()
            setComptesByEglise(old => ({ ...old, [egliseNumber]: data }))
        }
    }

    function updateLigne(idx, key, value) {
        setLignes(old => old.map((l, i) => i === idx ? { ...l, [key]: value } : l))
    }

    function setPaymodeField(idx, field, value) {
        setLignes(old => old.map((l, i) => {
            if (i !== idx) return l
            const resolved = typeof value === 'function' ? value(l.paymodeData[field]) : value
            return { ...l, paymodeData: { ...l.paymodeData, [field]: resolved } }
        }))
    }

    function buildFrequence(type, joursLibre, dateMoment) {
        const d = dateMoment
        switch (type) {
            case 'mensuel': return { type: 'mensuel', jour: d.date() }
            case 'trimestriel': return { type: 'trimestriel', jour: d.date() }
            case 'annuel': return { type: 'annuel', jour: d.date(), mois: d.month() + 1 }
            case 'libre': return { type: 'libre', jours: joursLibre.filter(j => j >= 1 && j <= 31) }
            default: return { type }
        }
    }

    function resetCreationForm() {
        setLibelle(''); setDate(null); setClasse(''); setFournisseur(''); setNumFacture('')
        setMontantDefaut(0); setReptotal(0); setPayMode(['']); setEspeces(0)
        setRepCheques([{ banque: '', montant: 0, num_cheque: '' }])
        setRepVir({ date: '', montant: 0, ref: '' }); setRepPrev({ date: '', montant: 0, ref: '' })
        setLignes([defaultLigne()]); setFreqType('mensuel'); setFreqJours([1])
    }

    async function handleCreate() {
        if (!date?.isValid()) return
        setSending(true)
        const paymode = {
            especes,
            cheques: repCheques.filter(c => c.montant > 0),
            virement: repVir.montant > 0 ? repVir : {},
            prelevement: repPrev.montant > 0 ? repPrev : {}
        }
        const body = {
            libelle, montant: montantDefaut, class: classe, fournisseur, num_facture: numFacture,
            paymode: JSON.stringify(paymode),
            lignes: JSON.stringify(lignes.map(l => ({
                egliseNumber: l.egliseNumber,
                montant: Number(l.montant),
                compteType: l.compteNum ? '' : l.compteType,
                compteNum: l.compteNum || '',
                paymode: l.paymodeData.useCustom ? {
                    especes: l.paymodeData.especes,
                    cheques: l.paymodeData.repCheques.filter(c => c.montant > 0),
                    virement: l.paymodeData.repVir.montant > 0 ? l.paymodeData.repVir : {},
                    prelevement: l.paymodeData.repPrev.montant > 0 ? l.paymodeData.repPrev : {}
                } : null
            }))),
            frequence: JSON.stringify(buildFrequence(freqType, freqJours, date)),
            prochaine_echeance: date.toISOString()
        }
        const res = await fetch(API.concat('/depenses-courantes'), {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify(body)
        })
        if (!handleTokenRefresh(res)) return
        if (res.ok) {
            const created = await res.json()
            setTemplates(old => [...old, created])
            setCreating(false)
            resetCreationForm()
        } else {
            alert('Erreur: ' + await res.text())
        }
        setSending(false)
    }

    async function handleToggleActive(template) {
        const res = await fetch(API.concat(`/depenses-courantes/${template._id}`), {
            method: 'PATCH', headers: authHeaders(),
            body: JSON.stringify({ active: !template.active })
        })
        if (!handleTokenRefresh(res)) return
        if (res.ok) {
            const updated = await res.json()
            setTemplates(old => old.map(t => t._id === updated._id ? updated : t))
        }
    }

    async function handleDelete(id) {
        if (!window.confirm('Supprimer cette dépense courante ?')) return
        const res = await fetch(API.concat(`/depenses-courantes/${id}`), { method: 'DELETE', headers: authHeaders() })
        if (!handleTokenRefresh(res)) return
        if (res.ok) setTemplates(old => old.filter(t => t._id !== id))
    }

    function openEdit(template) {
        setEditing(template)
        setEditDate(moment.utc(template.prochaine_echeance))
        setEditFreqType(template.frequence.type)
        setEditFreqJours(template.frequence.jours?.length > 0 ? [...template.frequence.jours] : [template.frequence.jour ?? 1])
        setEditLignes(template.lignes.map(l => ({ egliseNumber: l.egliseNumber, montant: l.montant, compteType: l.compteType || 'liaison', compteNum: l.compteNum || '', paymode: l.paymode ?? null })))
        template.lignes.forEach(l => fetchComptesEglise(l.egliseNumber))
    }

    async function handleEdit() {
        if (!editDate?.isValid()) return
        setSending(true)
        const res = await fetch(API.concat(`/depenses-courantes/${editing._id}`), {
            method: 'PATCH', headers: authHeaders(),
            body: JSON.stringify({
                frequence: buildFrequence(editFreqType, editFreqJours, editDate),
                prochaine_echeance: editDate.toISOString(),
                lignes: editLignes,
                montant: Math.max(...editLignes.map(l => l.montant))
            })
        })
        if (!handleTokenRefresh(res)) return
        if (res.ok) {
            const updated = await res.json()
            setTemplates(old => old.map(t => t._id === updated._id ? updated : t))
            setEditing(null)
        } else {
            alert('Erreur: ' + await res.text())
        }
        setSending(false)
    }

    const createDisabled = !libelle || !date?.isValid() || !classe || montantDefaut <= 0 || reptotal !== montantDefaut ||
        lignes.some(l => !l.egliseNumber || l.montant <= 0 || (!l.compteType && !l.compteNum) ||
            (l.paymodeData.useCustom && l.paymodeData.reptotal !== l.montant)) ||
        (freqType === 'libre' && freqJours.filter(j => j >= 1 && j <= 31).length === 0)

    const editDisabled = !editDate?.isValid() ||
        (editFreqType === 'libre' && editFreqJours.filter(j => j >= 1 && j <= 31).length === 0) ||
        editLignes.length === 0 ||
        editLignes.some(l => !l.egliseNumber || l.montant <= 0 || (!l.compteType && !l.compteNum))

    return (
        <Box display='flex' flexDirection='column' minHeight='100vh'>
            <Header />
            <Box sx={{ p: 3 }}>
                <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
                    <Typography variant='h5'>Dépenses courantes</Typography>
                    <Button variant='contained' startIcon={<Add />} onClick={() => setCreating(true)}>
                        Créer
                    </Button>
                </Stack>

                {loading ? <CircularProgress /> : templates.length === 0 ? (
                    <Typography color='text.secondary'>Aucune dépense courante configurée.</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {templates.map(t => (
                            <Box key={t._id} sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 2, opacity: t.active ? 1 : 0.55 }}>
                                <Stack direction='row' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1}>
                                    <Stack>
                                        <Typography fontWeight='bold'>{t.libelle}</Typography>
                                        <Typography variant='body2' color='text.secondary'>
                                            {freqLabel(t.frequence)} · {t.montant.toFixed(2)} € (défaut)
                                        </Typography>
                                        <Typography variant='body2' color='text.secondary'>
                                            {t.lignes.map(l => eglises.find(e => e.number === l.egliseNumber)?.nom || `Église n°${l.egliseNumber}`).join(' · ')}
                                        </Typography>
                                        <Typography variant='body2' color='text.secondary'>
                                            Prochaine échéance : {new Date(t.prochaine_echeance).toLocaleDateString('fr-FR')}
                                        </Typography>
                                    </Stack>
                                    <Stack direction='row' alignItems='center' spacing={1}>
                                        <FormControlLabel
                                            control={<Switch checked={t.active} onChange={() => handleToggleActive(t)} size='small' />}
                                            label={t.active ? 'Actif' : 'Inactif'}
                                        />
                                        <IconButton size='small' onClick={() => openEdit(t)}><Edit fontSize='small' /></IconButton>
                                        <IconButton size='small' color='error' onClick={() => handleDelete(t._id)}><Delete fontSize='small' /></IconButton>
                                    </Stack>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>

            {/* Dialog création */}
            <Dialog open={creating} onClose={() => { setCreating(false); resetCreationForm() }} maxWidth='md' fullWidth>
                <DialogTitle>Créer une dépense courante</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <Stack direction='row' spacing={2}>
                            <TextField required fullWidth label='Libellé' value={libelle} onChange={e => setLibelle(e.target.value)} />
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker label='Première échéance' value={date}
                                    onChange={v => { if (v?.isValid()) setDate(v) }}
                                    slotProps={{ textField: { size: 'small', required: true, sx: { minWidth: 180 } } }} />
                            </LocalizationProvider>
                        </Stack>

                        <Stack direction='row' spacing={2}>
                            <TextField required fullWidth select label='Classification' value={classe} onChange={e => setClasse(e.target.value)}>
                                {COMPTES_DE_CHARGES.sort((a, b) => a.libelle.localeCompare(b.libelle)).map((item, i) => (
                                    <MenuItem key={i} value={item.libelle}>{item.libelle}</MenuItem>
                                ))}
                            </TextField>
                            <TextField fullWidth label='Fournisseur' value={fournisseur} onChange={e => setFournisseur(e.target.value)} />
                            <TextField sx={{ minWidth: 150 }} label='N° Facture' value={numFacture} onChange={e => setNumFacture(e.target.value)} />
                        </Stack>

                        <TextField required type='number' label='Montant par défaut (€)' sx={{ width: 200 }}
                            slotProps={{ htmlInput: { min: '0', step: '0.01' } }}
                            value={montantDefaut}
                            onFocus={() => { if (montantDefaut === 0) setMontantDefaut('') }}
                            onBlur={() => { if (montantDefaut === '') setMontantDefaut(0) }}
                            onChange={e => {
                                const v = e.target.value.replace(',', '.')
                                if (/^\d+([.,]\d{1,2})?$/.test(e.target.value) && Number(v) >= 0) {
                                    const m = Number(v)
                                    setMontantDefaut(m)
                                    setLignes(old => old.map(l => ({ ...l, montant: m })))
                                } else if (e.target.value === '') setMontantDefaut('')
                            }} />

                        {montantDefaut > 0 && (
                            <PaymodeForm isdepense={true} total={montantDefaut} reptotal={reptotal} setReptotal={setReptotal}
                                pay_mode={payMode} setMode={setPayMode} especes={especes} setEspeces={setEspeces}
                                repCheques={repCheques} setRepCheques={setRepCheques} repVir={repVir} setRepVir={setRepVir}
                                repPrev={repPrev} setRepPrev={setRepPrev} have_banque={false} />
                        )}

                        <Typography variant='subtitle1' fontWeight='bold' mt={1}>Fréquence</Typography>
                        <Stack direction='row' spacing={2} alignItems='flex-start' flexWrap='wrap'>
                            <TextField select required label='Fréquence' value={freqType} onChange={e => setFreqType(e.target.value)} sx={{ minWidth: 180 }}>
                                <MenuItem value='mensuel'>Mensuel</MenuItem>
                                <MenuItem value='trimestriel'>Trimestriel</MenuItem>
                                <MenuItem value='annuel'>Annuel</MenuItem>
                                <MenuItem value='libre'>Dates libres</MenuItem>
                            </TextField>
                            {freqType !== 'libre' && date?.isValid() && (
                                <Typography variant='body2' color='text.secondary' sx={{ alignSelf: 'center' }}>
                                    {freqType === 'mensuel' && `Le ${date.date()} de chaque mois`}
                                    {freqType === 'trimestriel' && `Le ${date.date()} tous les 3 mois`}
                                    {freqType === 'annuel' && `Le ${date.date()} ${MOIS_LABELS[date.month()]} chaque année`}
                                </Typography>
                            )}
                            {freqType === 'libre' && (
                                <Stack spacing={1}>
                                    <Typography variant='body2' color='text.secondary'>Jours du mois :</Typography>
                                    {freqJours.map((j, i) => (
                                        <Stack key={i} direction='row' spacing={1} alignItems='center'>
                                            <TextField type='number' size='small' label={`Jour ${i + 1}`} sx={{ width: 100 }}
                                                slotProps={{ htmlInput: { min: 1, max: 31 } }}
                                                value={j}
                                                onChange={e => setFreqJours(old => old.map((v, k) => k === i ? Number(e.target.value) : v))} />
                                            {freqJours.length > 1 && (
                                                <IconButton size='small' onClick={() => setFreqJours(old => old.filter((_, k) => k !== i))}>
                                                    <ClearIcon fontSize='small' />
                                                </IconButton>
                                            )}
                                        </Stack>
                                    ))}
                                    <Button size='small' startIcon={<Add />} onClick={() => setFreqJours(old => [...old, 1])}>
                                        Ajouter un jour
                                    </Button>
                                </Stack>
                            )}
                        </Stack>

                        <Typography variant='subtitle1' fontWeight='bold' mt={1}>Églises concernées</Typography>
                        {lignes.map((ligne, idx) => (
                            <Stack key={idx} spacing={0.5} sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 1 }}>
                                <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                                    <TextField select required size='small' label='Église' sx={{ minWidth: 180 }} value={ligne.egliseNumber}
                                        onChange={e => {
                                            const num = Number(e.target.value)
                                            const eg = eglises.find(e => e.number === num)
                                            updateLigne(idx, 'egliseNumber', num)
                                            updateLigne(idx, 'compteNum', '')
                                            if (ligne.compteType === 'banque' && !eg?.have_banque) updateLigne(idx, 'compteType', 'liaison')
                                            fetchComptesEglise(num)
                                        }}>
                                        {eglises.sort((a, b) => a.nom.toLowerCase() < b.nom.toLowerCase() ? -1 : 1)
                                            .map(eg => <MenuItem key={eg._id} value={eg.number}>{eg.nom}</MenuItem>)}
                                    </TextField>
                                    <TextField required size='small' type='number' label='Montant (€)' sx={{ width: 120 }}
                                        slotProps={{ htmlInput: { min: '0.01', step: '0.01' } }}
                                        value={ligne.montant}
                                        onChange={e => updateLigne(idx, 'montant', Number(e.target.value))} />
                                    <TextField select required size='small' label='Type de compte' sx={{ minWidth: 160 }}
                                        value={ligne.compteType} disabled={!!ligne.compteNum}
                                        onChange={e => updateLigne(idx, 'compteType', e.target.value)}>
                                        <MenuItem value='liaison'>Compte de liaison</MenuItem>
                                        {eglises.find(e => e.number === ligne.egliseNumber)?.have_banque && <MenuItem value='banque'>Compte bancaire</MenuItem>}
                                        <MenuItem value='petite-caisse'>Petite caisse</MenuItem>
                                        <MenuItem value='interne'>Compte interne</MenuItem>
                                    </TextField>
                                    <TextField select size='small' label='Compte spécifique' sx={{ minWidth: 200 }}
                                        value={ligne.compteNum}
                                        onChange={e => updateLigne(idx, 'compteNum', e.target.value)}>
                                        <MenuItem value=''>— Type générique —</MenuItem>
                                        {(comptesByEglise[ligne.egliseNumber] || []).map(cp =>
                                            <MenuItem key={cp.num} value={cp.num}>{cp.libelle} ({cp.num_compte})</MenuItem>
                                        )}
                                    </TextField>
                                    <Button size='small' variant={ligne.paymodeData.useCustom ? 'contained' : 'outlined'} color='grey'
                                        sx={{ textTransform: 'none', whiteSpace: 'nowrap', minWidth: 'auto', fontSize: 11 }}
                                        onClick={() => setPaymodeField(idx, 'useCustom', !ligne.paymodeData.useCustom)}>
                                        {ligne.paymodeData.useCustom ? 'Paiement perso.' : 'Paiement commun'}
                                    </Button>
                                    {lignes.length > 1 && (
                                        <IconButton size='small' onClick={() => setLignes(old => old.filter((_, i) => i !== idx))}>
                                            <ClearIcon fontSize='small' />
                                        </IconButton>
                                    )}
                                </Stack>
                                {ligne.paymodeData.useCustom && ligne.montant > 0 && (
                                    <PaymodeForm isdepense={true}
                                        total={ligne.montant} reptotal={ligne.paymodeData.reptotal}
                                        setReptotal={v => setPaymodeField(idx, 'reptotal', v)}
                                        pay_mode={ligne.paymodeData.pay_mode}
                                        setMode={v => setPaymodeField(idx, 'pay_mode', v)}
                                        especes={ligne.paymodeData.especes}
                                        setEspeces={v => setPaymodeField(idx, 'especes', v)}
                                        repCheques={ligne.paymodeData.repCheques}
                                        setRepCheques={v => setPaymodeField(idx, 'repCheques', v)}
                                        repVir={ligne.paymodeData.repVir}
                                        setRepVir={v => setPaymodeField(idx, 'repVir', v)}
                                        repPrev={ligne.paymodeData.repPrev}
                                        setRepPrev={v => setPaymodeField(idx, 'repPrev', v)}
                                        have_banque={eglises.find(e => e.number === ligne.egliseNumber)?.have_banque ?? false}
                                    />
                                )}
                            </Stack>
                        ))}
                        <Button variant='contained' color='grey' startIcon={<Add />}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 'bold' }}
                            onClick={() => setLignes(old => [...old, { ...defaultLigne(), montant: montantDefaut || 0 }])}>
                            Ajouter une église
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setCreating(false); resetCreationForm() }}>Annuler</Button>
                    <Button variant='contained' disabled={createDisabled || sending} onClick={handleCreate}>
                        {sending ? <CircularProgress size={20} /> : 'Créer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog édition */}
            <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth='md' fullWidth>
                <DialogTitle>Modifier la dépense courante</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker label='Prochaine échéance' value={editDate}
                                onChange={v => { if (v?.isValid()) setEditDate(v) }}
                                slotProps={{ textField: { required: true } }} />
                        </LocalizationProvider>
                        <TextField select required label='Fréquence' value={editFreqType} onChange={e => setEditFreqType(e.target.value)}>
                            <MenuItem value='mensuel'>Mensuel</MenuItem>
                            <MenuItem value='trimestriel'>Trimestriel</MenuItem>
                            <MenuItem value='annuel'>Annuel</MenuItem>
                            <MenuItem value='libre'>Dates libres</MenuItem>
                        </TextField>
                        {editFreqType !== 'libre' && editDate?.isValid() && (
                            <Typography variant='body2' color='text.secondary'>
                                {editFreqType === 'mensuel' && `Le ${editDate.date()} de chaque mois`}
                                {editFreqType === 'trimestriel' && `Le ${editDate.date()} tous les 3 mois`}
                                {editFreqType === 'annuel' && `Le ${editDate.date()} ${MOIS_LABELS[editDate.month()]} chaque année`}
                            </Typography>
                        )}
                        {editFreqType === 'libre' && (
                            <Stack spacing={1}>
                                <Typography variant='body2' color='text.secondary'>Jours du mois :</Typography>
                                {editFreqJours.map((j, i) => (
                                    <Stack key={i} direction='row' spacing={1} alignItems='center'>
                                        <TextField type='number' size='small' label={`Jour ${i + 1}`} sx={{ width: 100 }}
                                            slotProps={{ htmlInput: { min: 1, max: 31 } }}
                                            value={j}
                                            onChange={e => setEditFreqJours(old => old.map((v, k) => k === i ? Number(e.target.value) : v))} />
                                        {editFreqJours.length > 1 && (
                                            <IconButton size='small' onClick={() => setEditFreqJours(old => old.filter((_, k) => k !== i))}>
                                                <ClearIcon fontSize='small' />
                                            </IconButton>
                                        )}
                                    </Stack>
                                ))}
                                <Button size='small' startIcon={<Add />} onClick={() => setEditFreqJours(old => [...old, 1])}>
                                    Ajouter un jour
                                </Button>
                            </Stack>
                        )}

                        <Typography variant='subtitle1' fontWeight='bold' mt={1}>Églises concernées</Typography>
                        {editLignes.map((ligne, idx) => (
                            <Stack key={idx} direction='row' spacing={1} alignItems='center' flexWrap='wrap'
                                sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 1 }}>
                                <TextField select required size='small' label='Église' sx={{ minWidth: 180 }} value={ligne.egliseNumber}
                                    onChange={e => {
                                        const num = Number(e.target.value)
                                        const eg = eglises.find(e => e.number === num)
                                        updateEditLigne(idx, 'egliseNumber', num)
                                        updateEditLigne(idx, 'compteNum', '')
                                        if (ligne.compteType === 'banque' && !eg?.have_banque) updateEditLigne(idx, 'compteType', 'liaison')
                                        fetchComptesEglise(num)
                                    }}>
                                    {eglises.sort((a, b) => a.nom.toLowerCase() < b.nom.toLowerCase() ? -1 : 1)
                                        .map(eg => <MenuItem key={eg._id} value={eg.number}>{eg.nom}</MenuItem>)}
                                </TextField>
                                <TextField required size='small' type='number' label='Montant (€)' sx={{ width: 120 }}
                                    slotProps={{ htmlInput: { min: '0.01', step: '0.01' } }}
                                    value={ligne.montant}
                                    onChange={e => updateEditLigne(idx, 'montant', Number(e.target.value))} />
                                <TextField select required size='small' label='Type de compte' sx={{ minWidth: 160 }}
                                    value={ligne.compteType} disabled={!!ligne.compteNum}
                                    onChange={e => updateEditLigne(idx, 'compteType', e.target.value)}>
                                    <MenuItem value='liaison'>Compte de liaison</MenuItem>
                                    {eglises.find(e => e.number === ligne.egliseNumber)?.have_banque && <MenuItem value='banque'>Compte bancaire</MenuItem>}
                                    <MenuItem value='petite-caisse'>Petite caisse</MenuItem>
                                    <MenuItem value='interne'>Compte interne</MenuItem>
                                </TextField>
                                <TextField select size='small' label='Compte spécifique' sx={{ minWidth: 200 }}
                                    value={ligne.compteNum}
                                    onChange={e => updateEditLigne(idx, 'compteNum', e.target.value)}>
                                    <MenuItem value=''>— Type générique —</MenuItem>
                                    {(comptesByEglise[ligne.egliseNumber] || []).map(cp =>
                                        <MenuItem key={cp.num} value={cp.num}>{cp.libelle} ({cp.num_compte})</MenuItem>
                                    )}
                                </TextField>
                                {editLignes.length > 1 && (
                                    <IconButton size='small' color='error' onClick={() => setEditLignes(old => old.filter((_, i) => i !== idx))}>
                                        <ClearIcon fontSize='small' />
                                    </IconButton>
                                )}
                            </Stack>
                        ))}
                        <Button variant='contained' color='grey' startIcon={<Add />}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 'bold' }}
                            onClick={() => setEditLignes(old => [...old, { egliseNumber: '', montant: editing?.montant || 0, compteType: 'liaison', compteNum: '', paymode: null }])}>
                            Ajouter une église
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditing(null)}>Annuler</Button>
                    <Button variant='contained' disabled={editDisabled || sending} onClick={handleEdit}>
                        {sending ? <CircularProgress size={20} /> : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Backdrop open={sending}><CircularProgress /></Backdrop>
        </Box>
    )
}
